import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import s from './styles.module.scss'
import { Alert, ImageTag, Modal } from 'components'
import { Encrypt } from 'helpers'
import { useSelector } from 'react-redux'
import * as api from 'api'
import { getAuthUserData } from 'utils/authManager'
import { logActivity } from 'utils/activityTracker'
import images from 'images'

const BusinessCard = React.memo(function BusinessCard({
  id,
  businessName,
  image,
  address,
  description,
  phoneNo,
  fkUserId,
  website,
  businessLocation
}) {
  const location = useSelector(state => state.location).location
  const userData = getAuthUserData()
  const navigate = useNavigate()
  const processing = useRef(false)

  const hasDirections = !!(businessLocation?.lat && businessLocation?.lng)

  const requirePlan = action => {
    if (!userData) {
      Modal.PrivacyModal()
      return
    }
    if (userData.plan === 'Plan 0') {
      Modal.Confirm('You need to buy a plan to use this feature', () => navigate('/renewPlan'))
      return
    }
    action()
  }

  const queryHandler = async (fkBusinessId = '', userId = '', query = '') => {
    if (processing.current) return
    processing.current = true

    const createLead = await api.publicApi.businessLeads.create({
      contactPerson: userData.name,
      phoneNo: userData.phoneNo,
      email: userData.email,
      query,
      state: userData.state,
      city: userData.city,
      fkBusinessId
    })

    if (createLead.code === 201) {
      await api.publicApi.notifications.create({
        notification: 'New query from ' + userData.name + ' ' + query,
        fkUserId: userId,
        redirect: ''
      })
      Alert.success('Your interest has been sent!')
      logActivity({
        eventType: 'business_lead_submitted',
        targetType: 'business',
        targetId: fkBusinessId,
        metadata: { query }
      })
    }
    processing.current = false
  }

  const openBusiness = () =>
    requirePlan(() => {
      logActivity({ eventType: 'business_detail_view', targetType: 'business', targetId: id })
      navigate('/' + location + '/' + Encrypt({ fkBusinessId: id }))
    })

  return (
    <div className={s.main}>
      <article className={s.card}>
        <div className={s.cardBody} onClick={openBusiness}>
          <div className={s.imageWrap}>
            <ImageTag src={image} alt={businessName} />
          </div>
          <div className={s.content}>
            <h3 className={s.title}>{businessName}</h3>
            {description && <p className={s.description}>{description}</p>}
            {address && (
              <p className={s.address}>
                <span className='material-icons-outlined'>location_on</span>
                {address}
              </p>
            )}
            <span className={s.viewLink}>
              View business
              <span className='material-icons-outlined'>arrow_forward</span>
            </span>
          </div>
        </div>

        <div className={s.actions} onClick={e => e.stopPropagation()}>
          {hasDirections && (
            <button
              type='button'
              className={s.directions}
              onClick={() =>
                requirePlan(() => {
                  logActivity({ eventType: 'business_directions_click', targetType: 'business', targetId: id })
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${businessLocation.lat},${businessLocation.lng}`,
                    '_blank'
                  )
                })
              }
            >
              <span className='material-icons-outlined'>directions</span>
              Directions
            </button>
          )}
          {website && (
            <button
              type='button'
              className={s.website}
              onClick={() =>
                requirePlan(() => {
                  logActivity({ eventType: 'business_website_click', targetType: 'business', targetId: id })
                  window.open(website.startsWith('http') ? website : `https://${website}`, '_blank')
                })
              }
            >
              <span className='material-icons-outlined'>language</span>
              Website
            </button>
          )}
          <button
            type='button'
            className={s.contact}
            onClick={() =>
              requirePlan(() => {
                logActivity({ eventType: 'business_contact_click', targetType: 'business', targetId: id })
                Modal.Confirm(
                  'Do you want to contact this business?',
                  () => queryHandler(id, fkUserId, 'Contact request'),
                  'Cancel'
                )
              })
            }
          >
            <span className='material-icons-outlined'>call</span>
            Contact
          </button>
          <button
            type='button'
            className={s.whatsapp}
            onClick={() =>
              requirePlan(() => {
                const message = `Hi, I'm interested in your business ${businessName}. Can you provide more details?`
                logActivity({ eventType: 'business_whatsapp_click', targetType: 'business', targetId: id })
                window.open(`https://wa.me/${String(phoneNo)}?text=${encodeURIComponent(message)}`, '_blank')
              })
            }
          >
            <img src={images.whatsapp} alt='' />
            WhatsApp
          </button>
        </div>
      </article>
    </div>
  )
})

BusinessCard.propTypes = {
  id: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  image: PropTypes.string,
  address: PropTypes.string,
  description: PropTypes.string,
  phoneNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkUserId: PropTypes.string,
  website: PropTypes.string,
  businessLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  })
}

BusinessCard.defaultProps = {
  image: '',
  address: '',
  description: '',
  phoneNo: '',
  fkUserId: '',
  website: '',
  businessLocation: { lat: null, lng: null }
}

export default BusinessCard
